// useLeagueBoard — read-only hook for the LEAGUES intelligence surface.
//
// Calls get_league_board(p_league) — a pure selector over the Weekly Substrate.
// Returns this week's fixtures for one canonical competition with our
// fair-price read, the market line, the CLV gap, and (when The Desk has spoken)
// the agents' verdict. Honest-empty when the league has no priced fixtures.
//
// The hook never writes. The weekly substrate fill + the Desk publish populate;
// this hook only displays.

import { useMemo } from "react";
import { useAuthSession } from "../../auth/useAuthSession.ts";
import { useRpcQuery } from "../../data/useRpcQuery.ts";

export interface LeagueMarketRead {
  market: string | null;
  selection: string | null;
  fair_prob: number | null;
  fair_odds: number | null;
  market_odds: number | null;
  clv_gap: number | null;
  conviction_tier: string | null;
  desk_confidence: number | null;
}

export interface LeagueFixture {
  fixture_id: string;
  home_team: string | null;
  away_team: string | null;
  competition: string | null;
  kickoff_at: string | null;
  intel_count: number | null;
  has_desk_verdict: boolean;
  headline: LeagueMarketRead | null;
  markets: LeagueMarketRead[];
}

export interface LeagueBoardState {
  fixtures: LeagueFixture[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useLeagueBoard(league: string): LeagueBoardState {
  const { user } = useAuthSession();
  const q = useRpcQuery<LeagueFixture[] | null>("get_league_board", {
    args: { p_league: league },
    enabled: Boolean(user),
    refetchInterval: 60_000,
  });
  const fixtures = useMemo(() => (Array.isArray(q.data) ? q.data : []), [q.data]);
  return useMemo<LeagueBoardState>(
    () => ({
      fixtures,
      loading: q.isLoading,
      error: q.error ? q.error.message : null,
      refetch: async () => { await q.refetch(); },
    }),
    [fixtures, q.isLoading, q.error, q.refetch],
  );
}
